import Row, {
  Props as AccordionRowProps,
} from "@storeblocks/accordion/lib/Row";
import styled, { css } from "styled-components";

import Accordion from "@storeblocks/accordion";
import Button from "@storeblocks/button";
import { Cell } from "@storeblocks/table-static";
import { Paragraph } from "App/global-style";
import CollapsibleContainer from "Components/CollapsibleContainer";
import { InsuranceTermsPdfButtons } from "Components/InsuranceTermsPdfButtons";
import { IpidPdfButtons } from "Components/IpidPdfButtons";
import ReactMarkdown from "react-markdown";

interface SelectableVariant {
  removePaddingLeft?: boolean;
}
export const CoverageWrap = styled.div<SelectableVariant>`
  @media screen and (min-width: 426px) {
    padding-left: ${({ removePaddingLeft }) =>
      removePaddingLeft ? "0px" : "var(--spacer-l)"};
  }

  width: 100%;
`;

export const StyledRadio = styled.input`
  margin-right: 8px;
`;

interface DescriptionProps {
  fw: number;
  size: string;
}

export const Description = styled(Paragraph)<DescriptionProps>`
  font-weight: ${({ fw }) => fw};
  margin: 10px 0;
  size: ${({ size }) => size};
`;

export const CoverageDescription = styled(ReactMarkdown)`
  p,
  ul {
    margin-bottom: 16px;
  }

  ul {
    list-style-position: inside;
  }
`;

export const Coverages = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  height: 24px;
  margin-bottom: 10px;

  @media screen and (max-width: 426px) {
    height: 48px;
  }

  & p {
    font-weight: 400;
    line-height: 24px;
    overflow-wrap: normal;
    size: 14px;
  }

  & svg {
    margin-right: 13px;
  }
`;

export const CoverageBoxes = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: 240px;
`;

interface Selectable {
  selected?: boolean;
}
export const CoverageAccordionHeader = styled.div<Selectable>`
  background-color: ${({ theme, selected }) =>
    (selected && theme.insuranceDetailsCard?.backgroundColor) || "inherit"};
  color: ${({ theme, selected }) =>
    (selected && theme.insuranceDetailsCard?.textColor) ?? "inherit"};
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-left: -16px;
  width: calc(100% + 16px);
`;

export const CoverageIcon = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding: 15px 8px 0 0;
`;

interface BoxProps {
  selected?: boolean;
}

export const Box = styled.div<BoxProps>`
  align-items: center;
  background: ${({ selected, theme }) => selected && theme.table.column};
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  max-width: 90px;
  min-width: 80px;
  padding: 4px;
  text-align: center;
  width: 100%;

  @media screen and (max-width: 768px) {
    max-width: 60px;
    min-width: 60px;
  }
`;

export const HeaderBox = styled(Box)`
  background: ${({ selected, theme }) => selected && theme.table.column};
`;

export const CoverageContent = styled(ReactMarkdown)`
  a,
  p {
    font-size: 14px;
    line-height: 24px;
  }

  p {
    padding: 0 16px;
  }

  & + div {
    margin-top: var(--spacer-s);
  }
`;

export const CoverageExplanation = styled.p<Selectable>`
  background-color: ${({ theme, selected }) =>
    (selected && theme.insuranceDetailsCard?.backgroundColor) || "inherit"};
  font-size: 14px;
  line-height: 24px;
  margin: -24px;
  padding: 24px;
  width: 100%;
`;

export const CoverageExplanationMarkdown = styled(CoverageExplanation)`
  & > p {
    font-size: 14px;
  }

  & > ul {
    font-size: ${({ theme }) => theme.fontSizes.xs};
    margin: 16px 0 16px 24px;

    & > li {
      margin-bottom: ${({ theme }) => theme.spacing.xs};
    }
  }
`;

export const CoverageExplanationBulletPoint = styled.p<Selectable>`
  background-color: ${({ theme, selected }) =>
    (selected && theme.insuranceDetailsCard?.backgroundColor) || "inherit"};
  font-size: 14px;
  line-height: 16px;
  margin: -24px;
  padding: 16px 24px 16px 40px;
  width: 100%;
`;

export const CoverageCaption = styled.p`
  font-size: 14px;
  font-weight: 400;
  padding: 12px 0;
  text-decoration: underline;

  :hover {
    text-decoration: none;
  }
`;

export const AccordionButton = styled(Button)`
  padding-left: 0;

  &:hover,
  &:active,
  &:focus {
    background-color: transparent;

    & > span {
      text-decoration: underline;
    }
  }
`;

export const LeftJustifiedCollapisbleContainer = styled(CollapsibleContainer)`
  display: flex;
  justify-content: left;
`;

const leftJustifiedButtonInWrapper = css`
  display: flex;
  justify-content: left;

  > a {
    padding-left: 0;
  }
`;

export const LeftJustifiedIpidLinks = styled(IpidPdfButtons)`
  ${leftJustifiedButtonInWrapper}
`;

export const LeftJustifiedInsuranceTermsLink = styled(InsuranceTermsPdfButtons)`
  ${leftJustifiedButtonInWrapper}
`;

export const StyledAccordion = styled(Accordion)`
  box-shadow: none;
  width: 100%;
`;

export interface RowProps extends AccordionRowProps {
  isSelected?: boolean;
  hideIcon?: boolean;
}

const HideIcon = css`
  & > i {
    display: none;
  }
`;

export const StyledAccordionRow = styled(Row)<RowProps>`
  background: ${({ isSelected, theme }) =>
    isSelected && theme.insuranceDetailsCard.backgroundColor};
  width: 100%;

  & button {
    background-color: ${({ isSelected, theme }) =>
      (isSelected && theme.insuranceDetailsCard.backgroundColor) || "inherit"};
    border-left: none;
    padding: 0 16px;

    ${({ hideIcon }) => hideIcon && HideIcon}

    :hover {
      background: none;
      border-left: none;
    }

    & span {
      display: none;
    }
  }

  & div {
    align-self: stretch;
    border-left: none;

    :hover {
      border-left: none;
    }
  }
`;

export const AccordionHeaderRow = styled(StyledAccordionRow)`
  & button {
    background: ${({ theme }) => theme.table.backgroundColor};
    cursor: default;

    ${({ hideIcon }) => hideIcon && HideIcon}

    & p {
      align-self: center;

      /* font-weight: 700; */
      font-weight: 600;
      padding: 12px 0;
    }

    :hover {
      background: ${({ theme }) => theme.table.hover};
    }
  }

  @media screen and (max-width: 768px) {
    p {
      font-size: 12px;
    }
  }
`;

export const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

export const ExtendCoverageContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

export const ExtendCoverageCheckbox = styled.input`
  @media screen and (min-width: 426px) {
    margin-right: -14px;
    transform: translateX(-24px);
  }
`;

export const OverflowBox = styled.div`
  overflow-x: scroll;

  * {
    overflow-wrap: break-word;
  }
`;

export const SelectableCell = styled(Cell)<BoxProps>`
  background: ${({ selected, theme }) => selected && theme.table.column};
  text-align: center;
`;
